import { useState, useEffect, useRef } from "react";
import { Report, ReportParameter } from "../../../types/reportTypes";
import { fetchApiData } from "../serviceFile";
import { resolveDateRangePreset } from "../../../shared/resolveDateRangePreset";
import moment from "moment";

interface FormValues {
  [key: string]: any;
}

interface Errors {
  [key: string]: string;
}

interface PopupState {
  [key: string]: {
    data: any[];
    selected: any | null;
    total: number;
    isOpen: boolean;
    pagination: { pageIndex: number; pageSize: number };
    searchQuery: string;
    rowSelection: { [key: string]: boolean };
    isFetchingMore?: boolean;
    // Single-column sort (mirrors the main Client List page); only consulted
    // for parameters whose apiEndpoint.QueryParams lists sortBy/sortDirection
    // -- see the queryParams reduce below.
    sorting?: { id: string; desc: boolean }[];
  };
}

// Helper function to safely get nested values from objects.
// Supports plain dot-notation ("data.customerList") as well as a "[*]"
// wildcard segment ("data[*].options") that maps over an array and
// flattens the resolved values from each element into a single array.
const getNestedValue = (obj: any, path: string): any => {
  const resolve = (current: any, segments: string[]): any => {
    if (current == null || segments.length === 0) return current;

    const [segment, ...rest] = segments;
    const isWildcard = segment.endsWith("[*]");
    const key = isWildcard ? segment.slice(0, -3) : segment;
    const value = key ? current?.[key] : current;

    if (!isWildcard) return resolve(value, rest);
    if (!Array.isArray(value)) return undefined;

    return value.flatMap((item: any) => {
      const result = resolve(item, rest);
      return Array.isArray(result)
        ? result
        : result !== undefined
          ? [result]
          : [];
    });
  };

  return resolve(obj, path.split("."));
};

// Minimum characters before a popup/dropdown search is sent to the server.
// Below it the query is treated as empty, so one or two stray characters don't
// fire a request per keystroke against an unfiltered result set.
export const SEARCH_MIN_CHARS = 3;

// The query actually sent for a given typed value. Returning "" (rather than
// the partial text) for anything shorter is what keeps this out of the loader
// effect's dependency list too — typing "ab" produces the same effective query
// as an empty box, so no refetch is triggered and no spinner appears.
const effectiveSearch = (typed?: string): string => {
  const trimmed = (typed ?? "").trim();
  return trimmed.length >= SEARCH_MIN_CHARS ? trimmed : "";
};

// Inverse of getNestedValue for the plain dot-notation case: builds the
// minimal nested object that holds `value` at `path`. Used when restoring a
// saved report, where a popup parameter's apiEndpoint.value can be a dotted
// path ("customer.id") that buildReportParams reads back out with
// getNestedValue. No "[*]" wildcard support — that form only ever appears in
// apiEndpoint.responseData (a read path), never in apiEndpoint.value.
const buildNestedValue = (path: string, value: any): any =>
  path
    .split(".")
    .reverse()
    .reduce((acc, key) => ({ [key]: acc }), value as any);

export const useReportForm = (selectedReport: Report | null) => {
  const [formValues, setFormValues] = useState<FormValues>({});
  const [errors, setErrors] = useState<Errors>({});
  const [popupState, setPopupState] = useState<PopupState>({});
  const [dropdownOptions, setDropdownOptions] = useState<{
    [key: string]: any[];
  }>({});
  const [loadingFields, setLoadingFields] = useState<{
    [key: string]: boolean;
  }>({});

  const validateField = (param: ReportParameter, value: any): string => {
    if (param.required) {
      if (!value) return `${param.label} is required`;
      if (param.multiSelect && Array.isArray(value) && value.length === 0) {
        return `${param.label} is required`;
      }
      if (
        param.category === "daterange" &&
        (!value.startDate || !value.endDate)
      ) {
        return `${param.label} is required`;
      }
    }

    if (param.category === "daterange" && value?.startDate && value?.endDate) {
      const format = param.validate?.format || "YYYY-MM-DD";
      const start = moment(value.startDate, format);
      const end = moment(value.endDate, format);
      if (!start.isValid() || !end.isValid()) {
        return `Invalid date format. Use ${format}`;
      }
    }

    if (param.category === "datepicker" && value) {
      const date = moment(value, param.validate?.format || "YYYY-MM-DD");
      if (
        !date.isValid() ||
        (param.validate?.format && date.format(param.validate.format) !== value)
      ) {
        return `Invalid date format. Use ${param.validate?.format}`;
      }
    }

    if (param.category === "timepicker" && value) {
      if (!moment(value, "HH:mm").isValid())
        return `Invalid time format. Use HH:mm`;
    }

    if (param.category === "number" && value && isNaN(Number(value))) {
      return `Must be a valid number`;
    }

    if (param.category === "text" && value && typeof value !== "string") {
      return `Must be a valid text`;
    }

    return "";
  };

  const handleInputChange = (paramLabel: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [paramLabel]: value }));
    setErrors((prev) => ({ ...prev, [paramLabel]: "" }));
  };

  const handleBlur = (paramLabel: string) => {
    if (!selectedReport) return;
    const param = selectedReport.parameters.find((p) => p.label === paramLabel);
    if (param)
      setErrors((prev) => ({
        ...prev,
        [paramLabel]: validateField(param, formValues[paramLabel]),
      }));
  };

  // Validates every parameter at once — the single source of truth for "is
  // the form submittable right now", shared by Generate Report and Save as My
  // Reports so neither can skip it independently. Writes the result to
  // `errors` (so it is actually shown, not just checked) and returns whether
  // the form passed, so a caller can bail out without having to re-read
  // `errors` itself.
  //
  // `isOptional` lets a caller waive the required check for some parameters
  // while still running their format checks (Save as My Reports uses this).
  const validateAll = (
    isOptional: (param: ReportParameter) => boolean = () => false,
  ): boolean => {
    if (!selectedReport) return true;
    const newErrors: Errors = {};
    selectedReport.parameters.forEach((param) => {
      const error = validateField(
        isOptional(param) ? { ...param, required: false } : param,
        formValues[param.label],
      );
      if (error) newErrors[param.label] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearForm = () => {
    setFormValues({});
    setErrors({});
    setDropdownOptions({});
    setPopupState({});
    setLoadingFields({});
  };

  // Bulk setter for Saved Reports ("My Reports"): replaces the whole field-value
  // set in one go, which handleInputChange (one field at a time) and clearForm
  // (wipes everything) between them can't express.
  //
  // `reportParams` is the REPORT-GENERATION PAYLOAD as it was saved — keyed by
  // each parameter's `reportParam` and holding primitives, e.g.
  // { customerId: "4fa8ab21-..." } — not this hook's own per-field state, which
  // is keyed by `label` and holds whole option objects for select/popup fields.
  // So this has to invert ReportForm's buildReportParams transform, field by
  // field, rather than assign straight through.
  //
  // The inverse is built so that re-running buildReportParams over the result
  // reproduces the saved payload exactly — that round-trip is what makes
  // "load a saved report, then Generate" correct.
  //
  // Deliberately replaces rather than merges, so loading leaves no stray values
  // from whatever was typed before. dropdownOptions/popupState are left alone:
  // they hold fetched OPTIONS, not the user's selection.
  const loadReportParams = (reportParams: unknown) => {
    // A saved row's ReportParams can legitimately be absent or unusable:
    // undefined/null (an older row, or a column that was never populated), ""
    // (a row written before the column became jsonb), or some non-object shape
    // that predates the POST-time validation. Any of those means "no saved
    // values" — fall back to an empty set so every field renders blank, rather
    // than throwing or leaving whatever the user had typed before in place.
    //
    // Note `?? {}` alone is NOT enough here: it catches null/undefined but lets
    // "" through, and indexing a string would silently yield undefined per
    // field instead of an explicit empty form.
    const params: Record<string, any> =
      reportParams && typeof reportParams === "object" && !Array.isArray(reportParams)
        ? (reportParams as Record<string, any>)
        : {};
    const values: FormValues = {};

    (selectedReport?.parameters ?? []).forEach((param) => {
      // A daterange occupies TWO payload keys ("startKey,endKey"), so it is
      // reassembled rather than read from a single one. The preset name isn't
      // in the payload and isn't recoverable — the explicit dates are what
      // actually drive the report.
      if (param.category === "daterange") {
        const [startKey, endKey] = (param.reportParam || "")
          .split(",")
          .map((k) => k.trim());
        if (!startKey || !endKey) return;
        if (params[startKey] === undefined || params[endKey] === undefined)
          return;
        values[param.label] = {
          startDate: params[startKey],
          endDate: params[endKey],
        };
        return;
      }

      const saved = params[param.reportParam];
      if (saved === undefined) return;

      if (param.category !== "select") {
        values[param.label] = saved;
        return;
      }

      if (param.popup) {
        // The payload holds only the identifier, but FormField renders a popup
        // field's label from the SELECTED ROW's own display fields
        // (apiEndpoint.displayValue, or attributes). A bare id wrapper has none
        // of them, so the field would fall back to its placeholder and look as
        // though nothing were selected.
        //
        // The rows for this parameter have usually already been fetched into
        // popupState by the loader effect below, so match the saved id against
        // them and restore the WHOLE row — that makes the name appear exactly
        // as it did when the report was saved.
        //
        // Falls back to the minimal wrapper that buildReportParams can still
        // read the id back out of (`item[apiEndpoint.value]`, or a nested path
        // when the key is dotted) — for an id on a page that has not been
        // fetched, or before the rows arrive. Generate stays correct either
        // way; only the visible label degrades.
        // String(...) on both sides: a saved id and the id the options API
        // returns are not guaranteed to be the same JS type (a saved "123"
        // vs. a freshly-fetched numeric 123, for instance) — restoreSelectionFromForm
        // and popupRowId already compare this way for the same reason.
        const idPath = param.apiEndpoint?.value ?? "value";
        const loadedRows: any[] = popupState[param.label]?.data ?? [];
        const toValue = (id: any) =>
          loadedRows.find(
            (row) => String(getNestedValue(row, idPath)) === String(id),
          ) ?? buildNestedValue(idPath, id);

        values[param.label] = param.multiSelect
          ? (Array.isArray(saved) ? saved : [saved]).map(toValue)
          : toValue(saved);
        return;
      }

      // Plain dropdown: match the saved primitive back to a loaded option so
      // the label renders. Options load asynchronously, so fall back to a
      // synthetic option — buildReportParams only reads `.value`, so Generate
      // stays correct either way.
      const options = dropdownOptions[param.label] ?? [];
      const toOption = (v: any) =>
        options.find((opt: any) => String(opt.value) === String(v)) ?? {
          value: v,
          label: String(v),
        };
      values[param.label] = param.multiSelect
        ? (Array.isArray(saved) ? saved : [saved]).map(toOption)
        : toOption(saved);
    });

    setFormValues(values);
    setErrors({});
  };

  // Reset all form state whenever the selected report ITSELF changes (keyed
  // on ReportCode, not the whole object, since selectedReport is a new
  // object reference on every selection even for the same report). Without
  // this, switching reports left old values/errors/popup state behind for
  // any parameter label reused across reports (e.g. "Start Date", "Client
  // List") — the defaults effect below merges new defaults with `prev`, so a
  // stale value from the previous report would otherwise win over the new
  // report's own default.
  useEffect(() => {
    clearForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport?.ReportCode]);

  // Pre-populate parameters that configure a `default`, once per report
  // selection, without ever overriding a value already present (a user edit,
  // or — for "select" — a match applied after options load, below). "select"
  // categories are skipped here: their default can only be resolved once
  // their options have actually loaded, since the stored value must be the
  // option object, not the raw default value.
  useEffect(() => {
    if (!selectedReport) return;

    const initialValues: FormValues = {};
    selectedReport.parameters.forEach((param) => {
      if (param.default === undefined || param.default === null) return;
      if (param.category === "select") return;

      switch (param.category) {
        case "datepicker": {
          const format = param.validate?.format || "YYYY-MM-DD";
          initialValues[param.label] =
            param.default === "today"
              ? moment().format(format)
              : param.default;
          break;
        }
        case "daterange": {
          const format = param.validate?.format || "YYYY-MM-DD";
          if (typeof param.default === "string") {
            const resolved = resolveDateRangePreset(param.default, null, null);
            if (resolved) {
              initialValues[param.label] = {
                preset: param.default,
                startDate: resolved.start
                  ? resolved.start.format(format)
                  : null,
                endDate: resolved.end ? resolved.end.format(format) : null,
              };
            } else {
              console.warn(
                `Report parameter "${param.label}" has an unrecognized default preset "${param.default}".`
              );
            }
          } else if (typeof param.default === "object") {
            initialValues[param.label] = param.default;
          }
          break;
        }
        default:
          initialValues[param.label] = param.default;
      }
    });

    if (Object.keys(initialValues).length) {
      // Spread `prev` last so a value the user already touched always wins
      // over a default.
      setFormValues((prev) => ({ ...initialValues, ...prev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport]);

  // Load data for select fields
  //
  // isNewReport guards against a stale-closure bug: when the user switches
  // reports, this effect's dependency array changes (selectedReport differs)
  // so it reruns in the SAME commit as the ReportCode-keyed reset effect
  // above -- but that reset's setPopupState({}) is only queued, not yet
  // applied, so this closure's own `popupState` still holds the PREVIOUS
  // report's leftover search/pagination/sort for any parameter label reused
  // across reports (e.g. "Client List"). Fetching with those stale query
  // params filled the new report's popup with the old report's filtered
  // results -- the search box itself read as cleared (searchQuery does
  // correctly land on "" once the async fetch's setPopupState(prev => ...)
  // runs against the by-then-cleared state), but the fetched `data` was
  // already wrong. Detecting "this run is for a just-selected report" lets
  // us use fresh defaults instead of trusting that stale closure value.
  const lastLoadedReportCode = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!selectedReport) return;
    const isNewReport = lastLoadedReportCode.current !== selectedReport.ReportCode;
    lastLoadedReportCode.current = selectedReport.ReportCode;

    const loadData = async () => {
      const selectParams =
        selectedReport.parameters?.filter(
          (p) => p.category === "select" && p.apiEndpoint?.apiUrl
        ) || [];

      await Promise.all(
        selectParams.map(async (param) => {
          const usesOffset = param.apiEndpoint?.QueryParams?.includes("offset");
          const currentPageIndex = isNewReport
            ? 0
            : popupState[param.label]?.pagination?.pageIndex || 0;
          const isScrollLoadMore = usesOffset && currentPageIndex > 0;

          if (!isScrollLoadMore) {
            setLoadingFields((prev) => ({ ...prev, [param.label]: true }));
          }
          try {
            const queryParams = param.apiEndpoint?.QueryParams?.reduce(
              (acc: Record<string, any>, key: string) => {
                const defaultPageSize = usesOffset ? 20 : 10;
                if (key === "limit")
                  acc[key] = isNewReport
                    ? defaultPageSize
                    : popupState[param.label]?.pagination?.pageSize || defaultPageSize;
                if (key === "page")
                  acc[key] = isNewReport
                    ? 1
                    : (popupState[param.label]?.pagination?.pageIndex || 0) + 1;
                if (key === "offset")
                  acc[key] = isNewReport
                    ? 0
                    : (popupState[param.label]?.pagination?.pageIndex || 0) *
                      (popupState[param.label]?.pagination?.pageSize || defaultPageSize);
                if (key === "search")
                  acc[key] = isNewReport
                    ? ""
                    : effectiveSearch(popupState[param.label]?.searchQuery);
                if (key === "type")
                  acc[key] = param.apiEndpoint?.apiUrl.includes("client")
                    ? "client_list"
                    : undefined;
                if (key === "sortBy" || key === "sortDirection") {
                  const activeSort = isNewReport
                    ? undefined
                    : popupState[param.label]?.sorting?.[0];
                  acc[key] =
                    key === "sortBy"
                      ? activeSort?.id
                      : activeSort
                        ? activeSort.desc
                          ? "desc"
                          : "asc"
                        : undefined;
                }
                return acc;
              },
              {}
            );

            const response = await fetchApiData(
              param.apiEndpoint!.apiUrl,
              queryParams
            );

            let options: any[] = [];
            if (param.popup) {
              const data = param.apiEndpoint!.responseData
                ? getNestedValue(response, param.apiEndpoint!.responseData) ||
                []
                : response.data || [];

              options = data.map((item: any) => ({
                value: item[param.apiEndpoint!.value],
                label: param.apiEndpoint!.attributes?.length
                  ? param
                    .apiEndpoint!.attributes.map(
                      (attr: any) => item[attr.value]
                    )
                    .join(" ")
                  : item[param.apiEndpoint!.value],
              }));
              const total = param.apiEndpoint!.count
                ? getNestedValue(response, param.apiEndpoint!.count) || 0
                : 0;
              const currentOffset = (queryParams as any)?.["offset"] ?? 0;
              setPopupState((prev: any) => ({
                ...prev,
                [param.label]: {
                  data:
                    usesOffset && currentOffset > 0
                      ? [...(prev[param.label]?.data || []), ...data]
                      : data,
                  selected: prev[param.label]?.selected || null,
                  total,
                  isOpen: prev[param.label]?.isOpen || false,
                  pagination: prev[param.label]?.pagination || {
                    pageIndex: 0,
                    pageSize: usesOffset ? 20 : 10,
                  },
                  searchQuery: prev[param.label]?.searchQuery || "",
                  rowSelection: prev[param.label]?.rowSelection || {},
                  isFetchingMore: false,
                  sorting: prev[param.label]?.sorting || [],
                },
              }));
            } else {
              let data: any[] = [];
              let usedResponseDataPath = false;

              // Use responseData if defined (e.g. "data.customerList" or
              // the "[*]" wildcard form "data[*].options")
              if (param.apiEndpoint!.responseData) {
                data =
                  getNestedValue(response, param.apiEndpoint!.responseData) ||
                  [];
                usedResponseDataPath = true;
              }
              // Special case: /option endpoint with no responseData path
              // configured — grab the first field's options directly
              else if (param.apiEndpoint!.apiUrl.includes("/option")) {
                data = response.data?.[0]?.options || [];
              }

              if (
                !usedResponseDataPath &&
                param.apiEndpoint!.apiUrl.includes("/option")
              ) {
                options = data.map((item: any) => ({
                  value: item.value ?? "",
                  label: item.label ?? "",
                }));
              } else {
                options = data?.map((item: any) => {
                  const value = item[param.apiEndpoint!.value] ?? item.value;

                  let label = "";
                  if (param.apiEndpoint!.attributes?.length) {
                    label = param
                      .apiEndpoint!.attributes.map(
                        (attr: any) => item[attr.value]
                      )
                      .filter(Boolean)
                      .join(" ");
                  } else if (item.label !== undefined) {
                    label = item.label;
                  } else {
                    label = String(value);
                  }

                  return { value, label };
                });
              }
              setDropdownOptions((prev) => ({
                ...prev,
                [param.label]: options,
              }));

              if (param.default !== undefined && param.default !== null) {
                setFormValues((prev) => {
                  // Never override a value already set — a user edit, or a
                  // default already applied on an earlier load of this effect.
                  if (prev[param.label] !== undefined) return prev;

                  // Sentinel: default to every option currently loaded,
                  // rather than a specific value. Only meaningful for
                  // multiSelect — a single-select can't hold "all" of them.
                  if (param.default === "selectAll") {
                    return param.multiSelect && options.length
                      ? { ...prev, [param.label]: options }
                      : prev;
                  }

                  const defaults = Array.isArray(param.default)
                    ? param.default
                    : [param.default];
                  const matched = options.filter((opt: any) =>
                    defaults.includes(opt.value)
                  );
                  if (!matched.length) return prev;

                  return {
                    ...prev,
                    [param.label]: param.multiSelect ? matched : matched[0],
                  };
                });
              }
            }
          } catch (err) {
            console.error(`Failed to load options for ${param.label}:`, err);
            setErrors((prev) => ({
              ...prev,
              [param.label]: `Failed to load ${param.label} options`,
            }));
            if (usesOffset) {
              setPopupState((prev) => ({
                ...prev,
                [param.label]: { ...prev[param.label], isFetchingMore: false },
              }));
            }
          } finally {
            if (!isScrollLoadMore) {
              setLoadingFields((prev) => ({ ...prev, [param.label]: false }));
            }
          }
        })
      );
    };

    loadData();
  }, [
    selectedReport,
    ...Object.keys(popupState).map(
      (key) => popupState[key].pagination?.pageIndex
    ),
    ...Object.keys(popupState).map(
      (key) => popupState[key].pagination?.pageSize
    ),
    // The EFFECTIVE query, not the raw typed text: keystrokes below the
    // minimum length leave this unchanged, so they neither refetch nor flip
    // the loading flag.
    ...Object.keys(popupState).map((key) =>
      effectiveSearch(popupState[key].searchQuery),
    ),
    ...Object.keys(popupState).map((key) => {
      const activeSort = popupState[key].sorting?.[0];
      return activeSort ? `${activeSort.id}:${activeSort.desc}` : undefined;
    }),
  ]);

  const onLoadMore = (paramLabel: string) => {
    setPopupState((prev) => ({
      ...prev,
      [paramLabel]: {
        ...prev[paramLabel],
        isFetchingMore: true,
        pagination: {
          ...prev[paramLabel]?.pagination,
          pageIndex: (prev[paramLabel]?.pagination?.pageIndex || 0) + 1,
        },
      },
    }));
  };

  return {
    formValues,
    errors,
    popupState,
    dropdownOptions,
    loadingFields,
    handleInputChange,
    handleBlur,
    clearForm,
    loadReportParams,
    validateField,
    validateAll,
    setPopupState,
    onLoadMore,
  };
};